import React, { useContext, useEffect } from "react";
import { secured } from "react-abac";
import { useTranslation } from "react-i18next";
import mixpanel from "mixpanel-browser";
import { useDispatch, useSelector } from "react-redux";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { AnimClassEnum, AnimOrderEnum, AuthoritiesEnum, CRUDActionEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import RestrictedSection from "~/components/RestrictedSection";
import { ButtonGroup, Icon, Link, Modal, Section, Spinner, Text } from "~/components-shared";
import CoreContext from "~/lib/context";
import topicSelectors from "~/store/topics/selectors";
import { useHistory, useParams } from "react-router-dom";
import topicActionCreators from "~/store/topics/actions";
import ErrorSection from "~/components/ErrorSection";
import useAbacExt from "~/hooks/abac";
import styled, { useTheme } from "styled-components";
import moment from "moment";
import TopicUpdateModal from "~/components/TopicUpdateModal";
import TopicFootprintsCard from "~/components/TopicFootprintsCard";
import TopicDeleteModal from "~/components/TopicDeleteModal";
import TopicSummaryCard from "~/components/TopicSummaryCard";
import { useQueryState } from "~/hooks/common";
import TopicInfoCard from "~/components/TopicInfoCard";
import TopicFootprintDeleteModal from "~/components/TopicFootprintDeleteModal";
import TopicTargetCreateModal from "~/components/TopicTargetCreateModal";
import TopicTargetUpdateModal from "~/components/TopicTargetUpdateModal";
import TopicTargetDeleteModal from "~/components/TopicTargetDeleteModal";
import TopicLabelModal from "~/components/TopicLabelModal";
import TopicStatusAlert from "~/components/TopicStatusAlert";
import TopicRunRetroModal from "~/components/TopicRunRetroModal";
import TopicBulkFootprintDeleteModal from "~/components/TopicBulkFootprintDeleteModal";
import TopicBulkFootprintMoveModal from "~/components/TopicBulkFootprintMoveModal";
import TopicFootprintCreateModal from "~/components/TopicFootprintCreateModal";
import TopicFootprintUpdateModal from "~/components/TopicFootprintUpdateModal";
import TopicTargetUpdateCompleteModal from "~/components/TopicTargetUpdateCompleteModal";
import TopicResourcesCard from "~/components/TopicResourcesCard";
import topicFootprintSelectors from "~/store/topicFootprints/selectors";
import { TopicFootprintActionEnum } from "~/store/topicFootprints/types";
import { TopicActionEnum } from "~/store/topics/types";
import TopicShareInSlackModal from "~/components/TopicShareInSlackModal";
import authSelectors from "~/store/auth/selectors";
const HeadingText = styled(Text)`
  max-width: 740px;
`;
const Topic = () => {
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const {
    setSidebar
  } = useContext(CoreContext);
  const {
    topicId
  } = useParams();
  const topic = useSelector(topicSelectors.getTopic(topicId));
  const status = useSelector(topicSelectors.getStatusByTopic(topicId));
  const action = useSelector(topicSelectors.getAction);
  const footprintAction = useSelector(topicFootprintSelectors.getAction);
  const error = useSelector(topicSelectors.getErrorByTopic(topicId));
  const labelStatus = useSelector(topicSelectors.getLabelStatus);
  const user = useSelector(authSelectors.getUser);
  const {
    userHas,
    userHasOneOf
  } = useAbacExt();
  const [queryParams, setQueryParams] = useQueryState({
    view: "summary"
  });
  const history = useHistory();
  useEffect(() => {
    setSidebar(undefined);
  }, [setSidebar]);
  useEffect(() => {
    if (!topic && status === StatusStateEnum.IDLE) {
      dispatch(topicActionCreators.fetchOneStarted(topicId));
    }
  }, [status, dispatch]);
  useEffect(() => {
    if (labelStatus === StatusStateEnum.IDLE) {
      dispatch(topicActionCreators.fetchLabelsStarted());
    }
  }, [labelStatus, dispatch]);
  if (!topic && status === StatusStateEnum.FAILED) {
    return /*#__PURE__*/React.createElement(ErrorSection, {
      error: error
    });
  }
  if (!topic) {
    return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Spinner, {
      center: true
    }));
  }
  const views = [{
    id: "summary",
    title: t("Topic.summary"),
    onClick: id => handleViewClick(t("Topic.summary"), id)
  }, {
    id: "footprints",
    title: t("Topic.footprints"),
    inProgress: topic.isRetroWorking,
    count: topic.footprintCount,
    onClick: id => handleViewClick(t("Topic.footprints"), id)
  }, {
    id: "details",
    title: t("Topic.details"),
    onClick: id => handleViewClick(t("Topic.details"), id)
  }];
  const handleMemberClick = id => {
    history.push(user.id === id ? RoutesEnum.PROFILE : RoutesEnum.MEMBER.replace(":memberId", id));
  };
  const handleBack = () => {
    history.push(RoutesEnum.TOPICS);
    mixpanel.track(`Topic – Back "${topic.name}"`);
  };
  const handleViewClick = (name, id) => {
    setQueryParams({
      view: id
    });
    mixpanel.track(`Topic – View "${name}"`);
  };
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Link, {
    onClick: handleBack,
    weight: "semiBold",
    color: "text",
    s: {
      mt: 0,
      ml: -3
    },
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "chevron",
    rotate: -90,
    s: {
      mt: -0.5
    }
  }), "Topics"), /*#__PURE__*/React.createElement(HeadingText, {
    element: "h1",
    s: {
      mb: 1
    },
    className: `${AnimOrderEnum.TWO} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, topic.name), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "regular",
    fontSize: 3.5,
    display: "block",
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`,
    s: {
      mb: 2
    },
    align: "right"
  }, t("Topic.createdBy"), " ", /*#__PURE__*/React.createElement(Link, {
    onClick: () => handleMemberClick(topic.createdBy.id),
    color: colors.text.secondary,
    underline: true
  }, topic.createdBy.firstName, " ", topic.createdBy.lastName), " ", t("Topic.on"), " ", moment(topic.createdAt).format("MMMM D, YYYY")), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(TopicStatusAlert, {
    topic: topic
  }), /*#__PURE__*/React.createElement(TopicInfoCard, {
    topic: topic,
    s: {
      p: 0,
      mb: 6
    }
  }), /*#__PURE__*/React.createElement(ButtonGroup, {
    options: views,
    selected: queryParams.view
  }), /*#__PURE__*/React.createElement(DndProvider, {
    backend: HTML5Backend
  }, queryParams.view === "summary" && /*#__PURE__*/React.createElement(TopicSummaryCard, {
    topic: topic,
    s: {
      p: 0,
      mt: 6
    }
  }), queryParams.view === "footprints" && /*#__PURE__*/React.createElement(TopicFootprintsCard, {
    topic: topic,
    s: {
      p: 0,
      mt: 6
    }
  }), queryParams.view === "details" && /*#__PURE__*/React.createElement(TopicResourcesCard, {
    topic: topic,
    s: {
      p: 0,
      mt: 6
    }
  })), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.UPDATE
  }, /*#__PURE__*/React.createElement(TopicUpdateModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.DELETE
  }, /*#__PURE__*/React.createElement(TopicDeleteModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: footprintAction === CRUDActionEnum.CREATE
  }, /*#__PURE__*/React.createElement(TopicFootprintCreateModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: footprintAction === CRUDActionEnum.UPDATE
  }, /*#__PURE__*/React.createElement(TopicFootprintUpdateModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: footprintAction === CRUDActionEnum.DELETE
  }, /*#__PURE__*/React.createElement(TopicFootprintDeleteModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: footprintAction === TopicFootprintActionEnum.BULK_DELETE
  }, /*#__PURE__*/React.createElement(TopicBulkFootprintDeleteModal, null)), userHas(AuthoritiesEnum.TOPICS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: footprintAction === TopicFootprintActionEnum.BULK_MOVE
  }, /*#__PURE__*/React.createElement(TopicBulkFootprintMoveModal, null)), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Modal, {
    open: action === TopicActionEnum.CREATE_TARGET
  }, /*#__PURE__*/React.createElement(TopicTargetCreateModal, null)), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Modal, {
    open: action === TopicActionEnum.UPDATE_TARGET
  }, /*#__PURE__*/React.createElement(TopicTargetUpdateModal, null)), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Modal, {
    open: action === TopicActionEnum.UPDATE_TARGET_COMPLETE
  }, /*#__PURE__*/React.createElement(TopicTargetUpdateCompleteModal, null)), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Modal, {
    open: action === TopicActionEnum.DELETE_TARGET
  }, /*#__PURE__*/React.createElement(TopicTargetDeleteModal, null)), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Modal, {
    open: action === TopicActionEnum.MANAGE_LABELS
  }, /*#__PURE__*/React.createElement(TopicLabelModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === TopicActionEnum.SHARE_IN_SLACK
  }, /*#__PURE__*/React.createElement(TopicShareInSlackModal, null)), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Modal, {
    open: action === TopicActionEnum.RUN_RETRO
  }, /*#__PURE__*/React.createElement(TopicRunRetroModal, null)));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.TOPICS_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(Topic), () => true);